import React, { useState, useEffect, useRef } from 'react';
import { Search, Phone, Mail, MessageSquare, Linkedin, Calendar } from 'lucide-react';

interface CounterData {
  id: string;
  icon: React.ReactNode;
  label: string;
  targetValue: number;
  suffix?: string;
}

const NumbersLiveSection: React.FC = () => {
  const [counters, setCounters] = useState<{ [key: string]: number }>({});
  const [isVisible, setIsVisible] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [actionTriggers, setActionTriggers] = useState<{ [key: string]: boolean }>({});
  const [currentIncrementIndex, setCurrentIncrementIndex] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  const counterData: CounterData[] = [
    {
      id: 'leads',
      icon: <Search className="w-6 h-6" />,
      label: 'Leads verified',
      targetValue: 12431
    },
    {
      id: 'calls',
      icon: <Phone className="w-6 h-6" />,
      label: 'Calls placed',
      targetValue: 28905
    },
    {
      id: 'emails',
      icon: <Mail className="w-6 h-6" />,
      label: 'Emails sent',
      targetValue: 19104
    },
    {
      id: 'whatsapp',
      icon: <MessageSquare className="w-6 h-6" />,
      label: 'WhatsApp msgs',
      targetValue: 9612
    },
    {
      id: 'linkedin',
      icon: <Linkedin className="w-6 h-6" />,
      label: 'LinkedIn actions',
      targetValue: 3487
    },
    {
      id: 'meetings',
      icon: <Calendar className="w-6 h-6" />,
      label: 'Meetings booked',
      targetValue: 226
    }
  ];

  // Custom hook for count-up animation
  const useCountUp = (targetValue: number, id: string, index: number, duration: number = 2000, delay: number = 0) => {
    const [count, setCount] = useState(targetValue);
    const [currentTarget, setCurrentTarget] = useState(targetValue);

    useEffect(() => {
      if (!isVisible) return;

      const timer = setTimeout(() => {
        // Set initial value and mark as initialized
        setCount(targetValue);
        setCurrentTarget(targetValue);
        setHasInitialized(true);
      }, delay);

      return () => clearTimeout(timer);
    }, [targetValue, delay, isVisible]);

    // Continuous increment every 2 seconds after initial animation
    useEffect(() => {
      if (!hasInitialized || !isVisible || currentIncrementIndex !== index) return;

      const timer = setTimeout(() => {
        setCurrentTarget(prev => prev + 1);
        setCount(prev => prev + 1);
        
        // Trigger action animation
        setActionTriggers(prev => ({ ...prev, [id]: true }));
        setTimeout(() => {
          setActionTriggers(prev => ({ ...prev, [id]: false }));
        }, 1000);
        
        // Move to next counter
        setCurrentIncrementIndex(prev => (prev + 1) % 6);
      }, 2000);

      return () => clearTimeout(timer);
    }, [hasInitialized, isVisible, id, index, currentIncrementIndex]);

    return count;
  };

  // Intersection Observer to trigger animation when section is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  // Start the sequential increment cycle
  useEffect(() => {
    if (!hasInitialized || !isVisible) return;

    const cycleTimer = setTimeout(() => {
      setCurrentIncrementIndex(0); // Start with first counter
    }, 1000); // Small delay after initialization

    return () => clearTimeout(cycleTimer);
  }, [hasInitialized, isVisible]);

  return (
    <section ref={sectionRef} className="relative py-8 sm:py-12 lg:py-20 px-4 sm:px-6">
      <div className="w-full max-w-7xl mx-auto">
        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center max-w-6xl mx-auto">
          
          {/* Left Column - Counter Boxes Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4 order-2 lg:order-1">
            {counterData.map((counter, index) => {
              const count = useCountUp(counter.targetValue, counter.id, index, 2000, index * 80);
              
              return (
                <div
                  key={counter.id}
                  className="relative group"
                  style={{ animationDelay: `${index * 80}ms` }}
                >
                  {/* Gradient Border Effect */}
                  <div className={`
                    relative p-[2px] rounded-xl sm:rounded-2xl transition-all duration-300 transform hover:scale-105
                    ${actionTriggers[counter.id] 
                      ? 'bg-gradient-to-r from-brand-green-teal via-salespad-cyan to-brand-green-teal animate-pulse' 
                      : 'bg-gradient-to-r from-brand-blue/60 via-brand-green-teal/60 to-brand-blue/60 hover:from-brand-blue/80 hover:via-brand-green-teal/80 hover:to-brand-blue/80'
                    }
                  `}>
                    <div className="bg-chip-bg rounded-xl sm:rounded-2xl px-4 sm:px-5 lg:px-6 py-4 sm:py-5 backdrop-blur-sm relative overflow-hidden">
                      {/* Icon and Numbers */}
                      <div className="flex items-center space-x-2 sm:space-x-3 mb-2">
                        <div className="text-brand-blue group-hover:text-brand-green-teal transition-colors duration-300 relative z-10">
                          {counter.icon}
                        </div>
                        <div className="text-text-primary font-bold text-lg sm:text-xl tabular-nums">
                          {formatNumber(count)}
                        </div>
                      </div>
                      
                      {/* Label */}
                      <div className="text-text-secondary text-xs sm:text-sm leading-tight">
                        {counter.label}
                      </div>
                      
                      {/* Mini illustration - positioned absolutely */}
                      <div className="absolute top-2 right-2 sm:top-3 sm:right-3 opacity-20 group-hover:opacity-40 transition-opacity duration-300">
                        {counter.id === 'leads' && (
                          <div className="relative">
                            <svg className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-brand-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            {actionTriggers['leads'] && (
                              <div className="absolute -top-1 -right-1 w-2 h-2 sm:w-3 sm:h-3 bg-brand-green-teal rounded-full animate-ping" />
                            )}
                            {actionTriggers['leads'] && (
                              <div className="absolute -top-2 right-0 text-xs text-brand-green-teal font-bold animate-bounce">+1</div>
                            )}
                          </div>
                        )}
                        {counter.id === 'calls' && (
                          <div className="relative">
                            <svg className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-brand-green-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            {actionTriggers['calls'] && (
                              <div className="absolute -top-1 -right-1 w-2 h-2 sm:w-3 sm:h-3 bg-brand-green-teal rounded-full animate-pulse" />
                            )}
                            {actionTriggers['calls'] && (
                              <div className="absolute -top-2 right-0 text-xs text-brand-green-teal font-bold animate-bounce">📞</div>
                            )}
                          </div>
                        )}
                        {counter.id === 'emails' && (
                          <div className="relative">
                            <svg className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-salespad-cyan" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {actionTriggers['emails'] && (
                              <div className="absolute top-1 right-1 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-salespad-cyan rounded-full animate-ping" />
                            )}
                            {actionTriggers['emails'] && (
                              <div className="absolute -top-2 -right-1 text-xs text-salespad-cyan animate-bounce">✉️</div>
                            )}
                          </div>
                        )}
                        {counter.id === 'whatsapp' && (
                          <div className="relative">
                            <svg className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-brand-green-teal" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                            </svg>
                            {actionTriggers['whatsapp'] && (
                              <div className="absolute top-0 right-0 w-2 h-2 sm:w-3 sm:h-3 bg-brand-green-teal rounded-full animate-ping" />
                            )}
                            {actionTriggers['whatsapp'] && (
                              <div className="absolute -top-2 -right-2 text-xs animate-bounce">💬</div>
                            )}
                          </div>
                        )}
                        {counter.id === 'linkedin' && (
                          <div className="relative">
                            <svg className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-brand-blue" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                            </svg>
                            {actionTriggers['linkedin'] && (
                              <div className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-brand-blue rounded-full animate-pulse" />
                            )}
                            {actionTriggers['linkedin'] && (
                              <div className="absolute -top-3 -right-2 text-xs text-brand-blue animate-bounce">🤝</div>
                            )}
                          </div>
                        )}
                        {counter.id === 'meetings' && (
                          <div className="relative">
                            <svg className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-salespad-cyan" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h6m-6 0l-2 9a2 2 0 002 2h8a2 2 0 002-2l-2-9m-8 0V9a2 2 0 012-2h4a2 2 0 012 2v0" />
                            </svg>
                            {actionTriggers['meetings'] && (
                              <div className="absolute top-1 right-1 w-2 h-2 sm:w-3 sm:h-3 bg-salespad-cyan rounded-full animate-ping" />
                            )}
                            {actionTriggers['meetings'] && (
                              <div className="absolute -top-2 -right-1 text-xs text-salespad-cyan animate-bounce">📅</div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Activity indicator */}
                      <div className="absolute top-1 right-1 sm:top-2 sm:right-2 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-brand-green-teal rounded-full animate-pulse opacity-60"></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right Column - Text Content */}
          <div className="space-y-4 sm:space-y-6 order-1 lg:order-2 text-left">
            {/* Headline */}
            <div className="space-y-3 sm:space-y-4">
              <h2 className="text-text-primary font-black text-2xl sm:text-3xl lg:text-4xl xl:text-5xl leading-tight tracking-tight">
                In the Last 7 Days
              </h2>
              <div className="w-16 sm:w-20 lg:w-24 h-1 bg-gradient-brand"></div>
            </div>

            {/* One-liner */}
            <p className="text-text-secondary text-base sm:text-lg lg:text-xl xl:text-2xl leading-relaxed">
              Live snapshot across every channel showing real activity from your AI sales team.
            </p>

            {/* Additional Context */}
            <div className="space-y-2 sm:space-y-3 hidden sm:block">
              <p className="text-text-primary font-medium text-base sm:text-lg">
                Your agents are working around the clock:
              </p>
              <ul className="space-y-1.5 sm:space-y-2 text-text-secondary text-sm sm:text-base">
                <li className="flex items-center space-x-2">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-brand-blue rounded-full animate-pulse flex-shrink-0"></div>
                  <span>Finding and verifying new prospects</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-brand-green-teal rounded-full animate-pulse flex-shrink-0" style={{ animationDelay: '0.5s' }}></div>
                  <span>Engaging across multiple channels</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-salespad-cyan rounded-full animate-pulse flex-shrink-0" style={{ animationDelay: '1s' }}></div>
                  <span>Converting conversations to meetings</span>
                </li>
              </ul>
            </div>
          </div>
          
        </div>
      </div>
    </section>
  );
};

export default NumbersLiveSection;